
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AdminSettings = () => {
  const { toast } = useToast();
  const [showBancaFlowBanner, setShowBancaFlowBanner] = useState(() => {
    return localStorage.getItem("bancaflow_banner_enabled") === "true";
  });

  const handleBancaFlowToggle = (checked: boolean) => {
    setShowBancaFlowBanner(checked);
    localStorage.setItem("bancaflow_banner_enabled", String(checked));
  };

  const [generalSettings, setGeneralSettings] = useState({
    siteName: "PALPITE GURU",
    siteDescription: "A plataforma mais avançada para previsões no jogo do bicho utilizando inteligência artificial.",
    maxPalpitesFreemium: "2",
    maxPalpitesPremium: "10",
  });

  const [aiSettings, setAiSettings] = useState({
    modeloIA: "gpt-4",
    temperaturaIA: "0.7",
    maxTokens: "1024",
    frequenciaPenalty: "0.5",
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNewUsers: true,
    emailPurchases: true,
    emailReports: true,
    pushNewUsers: false,
    pushPurchases: true,
  });

  const handleGeneralSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Configurações salvas",
      description: "As configurações gerais foram atualizadas com sucesso.",
    });
  };

  const handleAISubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Configurações de IA salvas",
      description: "As configurações de inteligência artificial foram atualizadas com sucesso.",
    });
  };

  const handleNotificationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Configurações de notificações salvas",
      description: "As preferências de notificação foram atualizadas com sucesso.",
    });
  };

  return (
    <Tabs defaultValue="general" className="space-y-6">
      <TabsList>
        <TabsTrigger value="general">Geral</TabsTrigger>
        <TabsTrigger value="ai">IA</TabsTrigger>
        <TabsTrigger value="notifications">Notificações</TabsTrigger>
      </TabsList>

      <TabsContent value="general">
        <Card>
          <CardHeader>
            <CardTitle>Configurações Gerais</CardTitle>
            <CardDescription>
              Configure os parâmetros básicos do sistema.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleGeneralSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="siteName">Nome do Site</Label>
                  <Input
                    id="siteName"
                    value={generalSettings.siteName}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        siteName: e.target.value,
                      })
                    }
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="siteDescription">Descrição do Site</Label>
                  <Input
                    id="siteDescription"
                    value={generalSettings.siteDescription}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        siteDescription: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="maxPalpitesFreemium">
                    Máximo de Palpites (Versão Gratuita)
                  </Label>
                  <Input
                    id="maxPalpitesFreemium"
                    type="number"
                    value={generalSettings.maxPalpitesFreemium}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        maxPalpitesFreemium: e.target.value,
                      })
                    }
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="maxPalpitesPremium">
                    Máximo de Palpites (Versão Premium)
                  </Label>
                  <Input
                    id="maxPalpitesPremium"
                    type="number"
                    value={generalSettings.maxPalpitesPremium}
                    onChange={(e) =>
                      setGeneralSettings({
                        ...generalSettings,
                        maxPalpitesPremium: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <Label htmlFor="bancaflowBanner" className="text-base">Banner BancaFlow</Label>
                  <p className="text-sm text-muted-foreground">
                    Exibir o banner de propaganda da BancaFlow abaixo do header em todas as páginas.
                  </p>
                </div>
                <Switch
                  id="bancaflowBanner"
                  checked={showBancaFlowBanner}
                  onCheckedChange={handleBancaFlowToggle}
                />
              </div>

              <Button type="submit">Salvar Configurações</Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="ai">
        <Card>
          <CardHeader>
            <CardTitle>Configuração da IA</CardTitle>
            <CardDescription>
              Configure os parâmetros de inteligência artificial.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAISubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="modeloIA">Modelo de IA</Label>
                  <Select
                    value={aiSettings.modeloIA}
                    onValueChange={(value) =>
                      setAiSettings({
                        ...aiSettings,
                        modeloIA: value,
                      })
                    }
                  >
                    <SelectTrigger id="modeloIA">
                      <SelectValue placeholder="Selecione o modelo de IA" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt-4">GPT-4 (Recomendado)</SelectItem>
                      <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Rápido)</SelectItem>
                      <SelectItem value="llama-3">Llama 3 (Baixo custo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="temperaturaIA">
                    Temperatura da IA (0-1)
                    <span className="ml-2 text-xs text-muted-foreground">
                      Atual: {aiSettings.temperaturaIA}
                    </span>
                  </Label>
                  <Input
                    id="temperaturaIA"
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={aiSettings.temperaturaIA}
                    onChange={(e) =>
                      setAiSettings({
                        ...aiSettings,
                        temperaturaIA: e.target.value,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Valores menores geram resultados mais previsíveis, valores maiores geram resultados mais criativos.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="maxTokens">
                    Máximo de tokens
                  </Label>
                  <Input
                    id="maxTokens"
                    type="number"
                    value={aiSettings.maxTokens}
                    onChange={(e) =>
                      setAiSettings({
                        ...aiSettings,
                        maxTokens: e.target.value,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Limite máximo de tokens para respostas da IA.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frequenciaPenalty">
                    Penalidade de frequência (0-2)
                    <span className="ml-2 text-xs text-muted-foreground">
                      Atual: {aiSettings.frequenciaPenalty}
                    </span>
                  </Label>
                  <Input
                    id="frequenciaPenalty"
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={aiSettings.frequenciaPenalty}
                    onChange={(e) =>
                      setAiSettings({
                        ...aiSettings,
                        frequenciaPenalty: e.target.value,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Reduz a probabilidade da IA repetir as mesmas informações.
                  </p>
                </div>
              </div>

              <Button type="submit">Salvar Configurações de IA</Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="notifications">
        <Card>
          <CardHeader>
            <CardTitle>Configurações de Notificações</CardTitle>
            <CardDescription>
              Gerencie como e quando você recebe notificações.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleNotificationSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Notificações por Email</h3>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="emailNewUsers" className="text-base">Novos usuários</Label>
                    <p className="text-sm text-muted-foreground">
                      Receba notificações quando novos usuários se cadastrarem.
                    </p>
                  </div>
                  <Switch
                    id="emailNewUsers"
                    checked={notificationSettings.emailNewUsers}
                    onCheckedChange={(checked) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        emailNewUsers: checked,
                      })
                    }
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="emailPurchases" className="text-base">Novas compras</Label>
                    <p className="text-sm text-muted-foreground">
                      Receba notificações quando houver novas compras.
                    </p>
                  </div>
                  <Switch
                    id="emailPurchases"
                    checked={notificationSettings.emailPurchases}
                    onCheckedChange={(checked) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        emailPurchases: checked,
                      })
                    }
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="emailReports" className="text-base">Relatórios</Label>
                    <p className="text-sm text-muted-foreground">
                      Receba relatórios semanais por email.
                    </p>
                  </div>
                  <Switch
                    id="emailReports"
                    checked={notificationSettings.emailReports}
                    onCheckedChange={(checked) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        emailReports: checked,
                      })
                    }
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Notificações Push</h3>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="pushNewUsers" className="text-base">Novos usuários</Label>
                    <p className="text-sm text-muted-foreground">
                      Receba notificações push quando novos usuários se cadastrarem.
                    </p>
                  </div>
                  <Switch
                    id="pushNewUsers"
                    checked={notificationSettings.pushNewUsers}
                    onCheckedChange={(checked) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        pushNewUsers: checked,
                      })
                    }
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="pushPurchases" className="text-base">Novas compras</Label>
                    <p className="text-sm text-muted-foreground">
                      Receba notificações push quando houver novas compras.
                    </p>
                  </div>
                  <Switch
                    id="pushPurchases"
                    checked={notificationSettings.pushPurchases}
                    onCheckedChange={(checked) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        pushPurchases: checked,
                      })
                    }
                  />
                </div>
              </div>

              <Button type="submit">Salvar Preferências de Notificação</Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default AdminSettings;
